import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Check, Crown, BookOpen, Zap, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import AppShell from '@/components/AppShell';
import CancellationFeedbackModal from '@/components/CancellationFeedbackModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  {
    id: 'basic',
    name: 'Basic',
    monthlyPrice: 10,
    annualPrice: 100,
    monthlyPlanId: 'basic_monthly',
    annualPlanId: 'basic_annual',
    icon: BookOpen,
    color: 'amber',
    features: [
      'Browse all 78 curriculum units (Grades 7-12)',
      'View vocabulary terms & definitions',
      'View key identifications',
      'View essay prompts & themes',
      'View Kansas HGSS standards alignment',
    ],
    excluded: [
      'Skeleton Notes',
      'Lecture Presentations',
      'AI Essay Grader',
      'Interactive Timeline & Maps',
      'Daily Questions',
      'Primary Source Browser',
      'State Assessment Projects',
      'Quizzes',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 25,
    annualPrice: 250,
    monthlyPlanId: 'pro_monthly',
    annualPlanId: 'pro_annual',
    icon: Crown,
    color: 'stone',
    popular: true,
    features: [
      'Everything in Basic, plus:',
      'Skeleton Notes (student & teacher versions)',
      'Lecture Presentations with timer',
      'AI Essay Grader with plagiarism detection',
      'Interactive Historical Timeline',
      'Map Explorer & Interactive World Map',
      'Daily Questions generator',
      'Full Primary Source Browser',
      'HGSS State Assessment Projects (CBA)',
      'Auto-Graded Vocabulary & ID Quizzes',
      'Annotation Tool for primary sources',
    ],
    excluded: [],
  },
];

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { user, token, subscription, isAdmin, hasTier } = useAuth();
  const [loadingTier, setLoadingTier] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [showCancelModal, setShowCancelModal] = useState(false);

  const handleSubscribe = async (tier) => {
    if (!user || !token) {
      toast.error('Please sign in first');
      navigate('/login');
      return;
    }
    const planId = billingInterval === 'annual' ? tier.annualPlanId : tier.monthlyPlanId;
    try {
      setLoadingTier(tier.id);
      const res = await axios.post(`${API}/subscription/checkout`, {
        tier: planId,
        origin_url: window.location.origin,
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoadingTier(null);
    }
  };

  const handleCancel = async () => {
    // Open the feedback modal — it'll call confirmedCancel() if the user submits.
    setShowCancelModal(true);
  };

  const confirmedCancel = async () => {
    try {
      await axios.post(`${API}/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subscription cancelled');
      window.location.reload();
    } catch {
      toast.error('Failed to cancel subscription');
    }
  };

  const currentTier = isAdmin ? 'pro' : subscription.tier;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#F9F8F4] text-[#D4AF37] px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-[0.18em] mb-4 border border-[#EBE7E0]">
            <Zap className="h-3.5 w-3.5" /> Choose Your Plan
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#14213D] tracking-tight mb-3">
            Access the full Atlas History library
          </h1>
          <p className="text-sm text-[#5A6577] max-w-lg mx-auto mb-6">
            Monthly or annual subscriptions for unlimited access to lessons, primary sources, interactive maps, and AI teaching tools. Cancel anytime.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center bg-[#F9F8F4] border border-[#EBE7E0] rounded-full p-1 mb-6" data-testid="billing-toggle">
            <button
              onClick={() => setBillingInterval('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                billingInterval === 'monthly'
                  ? 'bg-[#14213D] text-[#FDFBF7] shadow-sm'
                  : 'text-[#5A6577] hover:text-[#14213D]'
              }`}
              data-testid="billing-monthly-btn"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval('annual')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingInterval === 'annual'
                  ? 'bg-[#14213D] text-[#FDFBF7] shadow-sm'
                  : 'text-[#5A6577] hover:text-[#14213D]'
              }`}
              data-testid="billing-annual-btn"
            >
              Annual
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                billingInterval === 'annual' ? 'bg-[#D4AF37] text-[#14213D]' : 'bg-[#D4AF37]/20 text-[#D4AF37]'
              }`}>
                Save 17%
              </span>
            </button>
          </div>

          {isAdmin && (
            <div className="mt-2 inline-flex items-center gap-2 bg-[#F9F8F4] border border-[#D4AF37]/30 text-[#14213D] px-4 py-2 rounded-md text-xs" data-testid="admin-badge">
              <Crown className="h-3.5 w-3.5 text-[#D4AF37]" /> Admin — Full access included
            </div>
          )}
          {currentTier !== 'free' && !isAdmin && (
            <div className="mt-2 inline-flex items-center gap-2 bg-[#F9F8F4] border border-[#EBE7E0] text-[#14213D] px-4 py-2 rounded-md text-xs" data-testid="current-plan-badge">
              <Check className="h-3.5 w-3.5 text-[#D4AF37]" /> Current plan: <strong className="capitalize">{currentTier}</strong>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {TIERS.map((tier) => {
            const isCurrentTier = currentTier === tier.id;
            const isDowngrade = currentTier === 'pro' && tier.id === 'basic';
            const price = billingInterval === 'annual' ? tier.annualPrice : tier.monthlyPrice;
            const perMonth = billingInterval === 'annual' ? Math.round((tier.annualPrice / 12) * 100) / 100 : tier.monthlyPrice;
            const TierIcon = tier.icon;
            return (
              <Card key={tier.id} className={`relative overflow-hidden border ${tier.popular ? 'border-[#D4AF37] shadow-lg' : 'border-[#EBE7E0] shadow-sm'} bg-white`} data-testid={`tier-card-${tier.id}`}>
                {tier.popular && (
                  <div className="absolute top-0 right-0 bg-[#D4AF37] text-[#14213D] text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-1 rounded-bl-lg">
                    Most Popular
                  </div>
                )}
                <div className="h-1.5" style={{ background: tier.popular ? '#D4AF37' : '#14213D' }} />
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="p-2.5 rounded-lg bg-[#F9F8F4] border border-[#EBE7E0]">
                      <TierIcon className="h-5 w-5 text-[#14213D]" />
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A6577] font-semibold">{tier.popular ? 'Recommended' : 'Starter'}</p>
                      <h3 className="font-serif-display text-xl text-[#14213D] tracking-tight">{tier.name}</h3>
                    </div>
                  </div>

                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="font-serif-display text-4xl text-[#14213D]">${price}</span>
                    <span className="text-sm text-[#5A6577]">/{billingInterval === 'annual' ? 'year' : 'month'}</span>
                  </div>
                  {billingInterval === 'annual' && (
                    <p className="text-xs text-[#14213D] font-medium mb-5">
                      <span className="text-[#D4AF37]">${perMonth.toFixed(2)}/mo</span> — 2 months free
                    </p>
                  )}
                  {billingInterval === 'monthly' && <div className="mb-5" />}

                  <ul className="space-y-2 mb-6">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-[#2D3748]">
                        <Check className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {tier.excluded.length > 0 && (
                    <ul className="space-y-1 mb-6 pt-3 border-t border-[#EBE7E0]">
                      {tier.excluded.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-[#A0AEC0] line-through">
                          <span className="w-4 shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {isAdmin ? (
                    <Button disabled className="w-full bg-[#F9F8F4] text-[#14213D] border border-[#EBE7E0]" variant="outline" data-testid={`subscribe-${tier.id}-btn`}>
                      Included with Admin
                    </Button>
                  ) : isCurrentTier ? (
                    <div className="space-y-2">
                      <Button disabled className="w-full bg-[#14213D] text-[#FDFBF7] hover:bg-[#14213D]" data-testid={`subscribe-${tier.id}-btn`}>
                        <Check className="h-4 w-4 mr-2" /> Current Plan
                      </Button>
                      <Button variant="outline" size="sm" className="w-full text-[#9B2C2C] border-[#EBE7E0] hover:bg-[#FEF5F5]" onClick={handleCancel} data-testid="cancel-btn">
                        Cancel Subscription
                      </Button>
                    </div>
                  ) : isDowngrade ? (
                    <Button disabled className="w-full" variant="outline" data-testid={`subscribe-${tier.id}-btn`}>
                      Downgrade not available
                    </Button>
                  ) : (
                    <Button
                      className={`w-full ${tier.popular ? 'bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7]' : 'bg-white hover:bg-[#F9F8F4] text-[#14213D] border border-[#14213D]'}`}
                      onClick={() => handleSubscribe(tier)}
                      disabled={loadingTier === tier.id}
                      data-testid={`subscribe-${tier.id}-btn`}
                    >
                      {loadingTier === tier.id ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                      ) : (
                        `Subscribe — $${price}/${billingInterval === 'annual' ? 'yr' : 'mo'}`
                      )}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        <p className="text-center text-xs text-[#5A6577] mt-8 max-w-md mx-auto">
          All plans are view-only. No export or edit capabilities. Cancel anytime — access revoked immediately upon cancellation.
        </p>

        <CancellationFeedbackModal
          open={showCancelModal}
          onOpenChange={setShowCancelModal}
          onConfirmedCancel={confirmedCancel}
        />
      </div>
    </AppShell>
  );
}
